/**
 * useRecordConsent — appends a row to public.consent_records.
 *
 * LGPD compliance: every consent decision (audio recording, terms, privacy)
 * is persisted as an immutable event keyed by the internal profiles.id (UUID
 * resolved by `useProfileId`).
 *
 * Sprint 0 Tier 1 — see workspace/standalone/outputs/security/...
 */
import { useMutation } from "@tanstack/react-query";
import { useSupabaseClient } from "@/lib/supabase";
import { useProfileId } from "@/features/onboarding/hooks/useProfileId";

export type ConsentKind =
  | "audio_recording_v1"
  | "audio_upload_v1"
  | "transcript_upload_v1"
  | "terms_v1"
  | "privacy_v1"
  | "deletion_requested";

export interface ConsentInput {
  kind: ConsentKind;
  /** Optional related entity id (e.g. consulta id for audio_recording). */
  target_id?: string | null;
  /** Free-form metadata (e.g. patient_id, version). */
  metadata?: Record<string, unknown>;
}

export function useRecordConsent() {
  const { client } = useSupabaseClient();
  const { profileId } = useProfileId();

  return useMutation<{ id: string }, Error, ConsentInput>({
    mutationFn: async (input) => {
      if (!profileId) {
        throw new Error("Sessão não pronta. Tente novamente.");
      }
      const { data, error } = await client
        .from("consent_records" as never)
        .insert({
          user_id: profileId,
          kind: input.kind,
          target_id: input.target_id ?? null,
          metadata: input.metadata ?? {},
        } as never)
        .select("id")
        .single();
      if (error) throw new Error(error.message);
      return data as { id: string };
    },
  });
}
